import { motion } from 'framer-motion';
import { MapPin, Video } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ProfessionalSessionCreationMode =
	| 'video-call'
	| 'transcription-only';

type ProfessionalSessionTypeSelectorProps = {
	value: ProfessionalSessionCreationMode;
	onChange: (value: ProfessionalSessionCreationMode) => void;
	className?: string;
};

const OPTIONS: Array<{
	value: ProfessionalSessionCreationMode;
	title: string;
	description: string;
	icon: typeof Video;
}> = [
	{
		value: 'video-call',
		title: 'online',
		description:
			'Realize a consulta com vídeo e áudio dentro da plataforma da Plumaa.',
		icon: Video,
	},
	{
		value: 'transcription-only',
		title: 'presencial',
		description:
			'Use o sistema plumaa para evoluir o prontuário automaticamente.',
		icon: MapPin,
	},
];

const ProfessionalSessionTypeSelector = ({
	value,
	onChange,
	className,
}: ProfessionalSessionTypeSelectorProps) => {
	const selectedIndex = OPTIONS.findIndex((option) => option.value === value);

	return (
		<div className={cn('w-full max-w-[42rem]', className)}>
			<div className="relative grid grid-cols-2 gap-1 rounded-2xl border border-zinc-200 bg-zinc-50 p-1">
				<motion.div
					layout
					className="absolute inset-y-1 rounded-xl border border-orange-700 bg-orange-100 shadow-sm"
					style={{
						width: 'calc(50% - 4px)',
						left: selectedIndex === 0 ? 4 : 'calc(50% + 0px)',
					}}
					transition={{ type: 'spring', stiffness: 380, damping: 32 }}
				/>

				{OPTIONS.map((option) => {
					const Icon = option.icon;
					const isSelected = option.value === value;

					return (
						<button
							key={option.value}
							type="button"
							onClick={() => onChange(option.value)}
							className={cn(
								'relative z-10 flex min-h-[5rem] flex-col items-start  gap-2 rounded-xl px-3 py-2 text-left transition-colors',
								isSelected
									? 'text-orange-900'
									: 'text-zinc-500 hover:text-zinc-700',
							)}
						>
							<span className="flex items-center justify-center gap-2 text-sm font-bold">
								<Icon
									className="size-4 shrink-0"
									strokeWidth={2.25}
								/>
								{option.title}
							</span>
							<span
								className={cn(
									'text-xs leading-relaxed',
									isSelected
										? 'text-orange-900'
										: 'text-zinc-400',
								)}
							>
								{option.description}
							</span>
						</button>
					);
				})}
			</div>
		</div>
	);
};

export { ProfessionalSessionTypeSelector };
